import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './UserMenu.css';

interface UserMenuProps {
  preferencesPath: string;
  onLogout: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ preferencesPath, onLogout }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return null;

  const displayName = user.name || user.email || '';
  const initial = (displayName.trim()[0] || '?').toUpperCase();

  return (
    <div className="user-menu" ref={dropdownRef}>
      <button
        type="button"
        className="user-menu-toggle"
        onClick={() => setIsOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <span className="user-menu-avatar" aria-hidden="true">{initial}</span>
        <span className="user-menu-name">{displayName}</span>
        <span className="user-menu-arrow" aria-hidden="true">▾</span>
      </button>

      {isOpen && (
        <div className="user-menu-dropdown" role="menu">
          <div className="user-menu-header">
            <div className="user-menu-header-name">{displayName}</div>
            {user.email && user.email !== displayName && (
              <div className="user-menu-header-email">{user.email}</div>
            )}
          </div>
          <Link
            to={preferencesPath}
            className="user-menu-item"
            role="menuitem"
            onClick={() => setIsOpen(false)}
          >
            <i className="fas fa-sliders-h user-menu-item-icon" aria-hidden="true" />
            <span>{t('nav.preferences')}</span>
          </Link>
          <button
            type="button"
            className="user-menu-item user-menu-item--danger"
            role="menuitem"
            onClick={() => {
              setIsOpen(false);
              onLogout();
            }}
          >
            <i className="fas fa-sign-out-alt user-menu-item-icon" aria-hidden="true" />
            <span>{t('nav.logout')}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
